import { useEffect, useRef, useState } from "react"
import { useInertiaCable } from "@inertia-cable/react"
import { Send, RotateCw, MessagesSquare, Sparkles, Check } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ForgeButton } from "@/components/ui/forge-button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/cn"
import { ChatMessageBubble } from "@/components/domain/chat-message-bubble"
import { ChatThinkingIndicator } from "@/components/domain/chat-thinking-indicator"
import type { ChatBubble, TestLabAgentOption } from "@/types/test_lab"
import { usePage } from "@inertiajs/react"
import type { SharedProps } from "@/types/inertia"

type Props = {
  skillId: number
  skillKey: string
  /** SkillVersion id to pin the test convo to when scope === "skill_only".
      Backend (StartAgentTestConvo → AgentConvo.test_skill_version_ids →
      PromptComposer) keys off SkillVersion ids; passing the parent Skill
      id here would filter to nothing. Parent passes `skill.versionId`
      (the working version: draft || live || latest). */
  defaultSkillVersionId: number
  /** Agent options the operator can pick from (the company's agents). */
  agents: TestLabAgentOption[]
  /** Default agent (skill's primary attachment, or first company agent). */
  defaultAgentId: number | null
  testStream: string
  /** Per-staff persisted source_key for THIS skill. Hydrates the field on
   *  load and is auto-updated by `PATCH /skills/:id/test/source_key` as
   *  the operator types (debounced). */
  initialSourceKey?: string
  /** Click "Discuss with Forge" → parent fetches the synthesized message,
      switches to the Design tab, seeds the composer. */
  onDiscussConvo?: (convoId: number) => void
}

type ConvoState =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "ready"; convoId: number; convoUuid: string; appuserId: number }
  | { kind: "error"; message: string }

export function TestPane({ skillId, skillKey, defaultSkillVersionId, agents, defaultAgentId, testStream, initialSourceKey = "", onDiscussConvo }: Props) {
  const [agentId, setAgentId]       = useState<number | null>(defaultAgentId)
  const [scope, setScope]           = useState<"skill_only" | "all_skills">("skill_only")
  const [convo, setConvo]           = useState<ConvoState>({ kind: "idle" })
  const [messages, setMessages]     = useState<ChatBubble[]>([])
  const [draft, setDraft]           = useState("")
  const [sending, setSending]       = useState(false)
  const [resetNonce, setResetNonce] = useState(0)
  // Optional source_key — when present, the test convo runs as that
  // existing appuser (banned, locked, has-payouts, etc.). Empty string
  // → fresh ephemeral redteam-prefixed appuser per session. Hydrated
  // from CompanyUser.test_lab_source_keys[skill_id].
  const [sourceKey, setSourceKey]       = useState<string>(initialSourceKey)
  // Buffered draft of the source-key field — committed to `sourceKey`
  // (and persisted server-side) on a 600ms debounce after typing stops,
  // or immediately on blur / Enter. No Apply button. Typing without
  // debouncing would respawn the convo on every keystroke.
  const [sourceKeyDraft, setSourceKeyDraft] = useState<string>(initialSourceKey)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  // useInertiaCable subscribes ONCE per mount and captures its onMessage
  // closure at that moment. Keep convo in a ref so the live broadcast
  // handler always sees the current convoId, not the stale "idle" / "starting"
  // value from the first render.
  const convoRef = useRef<ConvoState>({ kind: "idle" })
  useEffect(() => { convoRef.current = convo }, [convo])

  // Source-key autosave status — drives the inline green-check indicator.
  // "saved" auto-clears after 1.5s so the check doesn't linger forever.
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle")
  useEffect(() => {
    if (saveStatus !== "saved") return
    const t = window.setTimeout(() => setSaveStatus("idle"), 1500)
    return () => window.clearTimeout(t)
  }, [saveStatus])

  // Source-key autosave: debounce typing → commit to sourceKey (which
  // respawns the convo) AND persist server-side. Blur / Enter call
  // commitSourceKey directly (no wait). Empty value clears the saved
  // entry, matching the controller's delete-on-empty behavior.
  function commitSourceKey(next: string) {
    const trimmed = next.trim()
    setSourceKeyDraft(trimmed)
    setSourceKey(trimmed)
    setSaveStatus("saving")
    const body = new URLSearchParams()
    body.set("source_key", trimmed)
    fetch(`/skills/${skillId}/test/source_key`, {
      method: "PATCH", headers: csrfHeaders(), credentials: "same-origin", body,
    })
      .then((r) => { if (r.ok) setSaveStatus("saved"); else setSaveStatus("idle") })
      .catch(() => setSaveStatus("idle"))
  }
  // Debounce committing while the user is still typing.
  useEffect(() => {
    if (sourceKeyDraft === sourceKey) return
    const t = window.setTimeout(() => commitSourceKey(sourceKeyDraft), 600)
    return () => window.clearTimeout(t)
  // commitSourceKey is referentially stable enough — the only reactive
  // input is sourceKeyDraft. Re-running on sourceKey change is fine.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKeyDraft, sourceKey])

  // Spin up a fresh ephemeral convo whenever agentId/scope changes or
  // the operator hits Reset. Previous convo is orphaned (redteam-
  // prefixed appuser, so it won't pollute production analytics).
  useEffect(() => {
    if (!agentId) {
      setConvo({ kind: "idle" })
      setMessages([])
      return
    }
    setConvo({ kind: "starting" })
    setMessages([])
    const body = new URLSearchParams()
    body.set("agent_id", String(agentId))
    // skill_ids[] holds SkillVersion ids — StartAgentTestConvo writes them
    // into agent_convos.test_skill_version_ids, which PromptComposer
    // resolves via SkillVersion.where(id: ...). Passing a parent Skill id
    // here would filter the prompt down to NO skills (silent breakage).
    if (scope === "skill_only") body.append("skill_ids[]", String(defaultSkillVersionId))
    if (sourceKey.trim()) body.set("source_key", sourceKey.trim())
    fetch(`/skills/${skillId}/test/agent_chat/start`, {
      method: "POST", headers: csrfHeaders(), credentials: "same-origin", body,
    })
      .then(async (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then((d) => {
        setConvo({ kind: "ready", convoId: d.convo_id, convoUuid: d.convo_uuid, appuserId: d.appuser_id })
        refetchMessages(skillId, d.convo_id).then(setMessages)
      })
      .catch((e) => setConvo({ kind: "error", message: e.message }))
  }, [agentId, scope, skillId, defaultSkillVersionId, resetNonce, sourceKey])

  // InertiaCable refresh payloads (the kind `broadcasts_to` produces)
  // fire onRefresh, NOT onMessage. onMessage is for the separate
  // {type:"message",data} channel. We refetch on ANY AgentMessage
  // refresh and let the convo_id query param scope the response.
  useInertiaCable(testStream, {
    only: [],
    debounce: 100,
    onRefresh: (payload) => {
      if (payload.model !== "AgentMessage") return
      const c = convoRef.current
      if (c.kind !== "ready") return
      refetchMessages(skillId, c.convoId).then(setMessages)
    },
  })

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages.length])

  async function send() {
    if (sending || !draft.trim() || convo.kind !== "ready") return
    setSending(true)
    const text = draft
    setDraft("")
    const optimistic: ChatBubble = {
      id: -Date.now(),
      role: "user",
      content: text,
      createdAt: new Date().toISOString(),
    }
    setMessages((m) => [...m, optimistic])
    const body = new URLSearchParams({ convo_id: String(convo.convoId), content: text })
    try {
      await fetch(`/skills/${skillId}/test/agent_chat`, {
        method: "POST", headers: csrfHeaders(), credentials: "same-origin", body,
      })
      // The controller blocks until TurnLoop completes — by the time we
      // get here, BOTH the user-turn AND assistant-turn AgentMessages are
      // already in the DB. Refetch immediately so we don't depend on the
      // cable broadcast (which has been flaky in dev). Cable still acts as
      // a backup for messages that arrive outside the send/response cycle.
      const c = convoRef.current
      if (c.kind === "ready") {
        const fresh = await refetchMessages(skillId, c.convoId)
        setMessages(fresh)
      }
    } finally {
      setSending(false)
    }
  }

  function resetConvo() {
    if (!confirm("Start a fresh ephemeral convo? Current messages will be discarded.")) return
    setResetNonce((n) => n + 1)
  }

  const scopeLabel   = scope === "skill_only" ? `Only ${skillKey}` : "All skills"
  const activeAgent  = agents.find((a) => a.id === agentId) ?? null
  const currentUser  = usePage<SharedProps>().props.current_user
  const lastMsg      = messages[messages.length - 1]
  // "Thinking" shows whenever we're awaiting an agent reply: either we're
  // still posting the user turn, or we've already posted but the cable
  // broadcast for the assistant message hasn't refetched yet (visible state:
  // last message is the user's optimistic / persisted turn).
  const pendingReply = sending || (lastMsg && lastMsg.role === "user")

  return (
    <div className="flex flex-col gap-3">
      {/* Header strip */}
      <Card className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <MessagesSquare size={14} strokeWidth={1.7} className="text-app-muted" />
          <div>
            <h3 className="text-sm font-semibold">Test against the agent</h3>
            <p className="text-[10px] text-app-subtle">
              {activeAgent ? `Talking to ${activeAgent.name}` : "Pick an agent to start"} · {scopeLabel}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={agentId ?? ""}
            onChange={(e) => setAgentId(e.target.value ? Number(e.target.value) : null)}
            className="rounded-control border border-border bg-app-panel2 px-2 py-1 text-xs text-app-text"
            aria-label="Pick agent"
          >
            {agents.length === 0 && <option value="">No agents in company</option>}
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <ScopeToggle value={scope} onChange={setScope} />
          {/* Source-key input — empty means a fresh ephemeral redteam-
              prefixed appuser; filled means re-use that real appuser
              so the convo runs against their actual state. We only
              commit on Apply / Enter so we don't respawn a convo on
              every keystroke. */}
          <div className="relative inline-flex items-center">
            <input
              type="text"
              value={sourceKeyDraft}
              onChange={(e) => setSourceKeyDraft(e.target.value)}
              onBlur={() => commitSourceKey(sourceKeyDraft)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  commitSourceKey(sourceKeyDraft)
                }
              }}
              placeholder="source_key (optional)"
              className="w-44 rounded-control border border-border bg-app-panel2 px-2 py-1 pr-7 text-xs text-app-text placeholder:text-app-subtle"
              aria-label="Run as a specific appuser by source_key — autosaves on blur"
              title="Autosaves on blur / Enter / pause. Empty clears the saved value."
            />
            {saveStatus === "saved" && (
              <Check
                size={14}
                strokeWidth={2.5}
                className="pointer-events-none absolute right-2 text-status-success"
                aria-label="Saved"
              />
            )}
          </div>
          <ForgeButton
            size="sm"
            onClick={() => convo.kind === "ready" && onDiscussConvo?.(convo.convoId)}
            disabled={convo.kind !== "ready" || messages.length === 0 || !onDiscussConvo}
            title={
              convo.kind !== "ready" ? "Start a convo first"
              : messages.length === 0 ? "Send a message first"
              : "Open the Design tab with this convo pre-filled for Forge"
            }
          >
            Discuss with Forge
          </ForgeButton>
          <Button type="button" size="sm" variant="ghost" onClick={resetConvo} disabled={convo.kind === "starting"}>
            <RotateCw size={12} strokeWidth={2} className="mr-1" /> Reset convo
          </Button>
        </div>
      </Card>

      {/* Chat thread */}
      <Card className="flex flex-col p-0">
        <div ref={scrollRef} className="flex-1 max-h-[min(60vh,640px)] overflow-y-auto px-6 py-6 space-y-6">
          {convo.kind === "starting" && <p className="text-xs text-app-muted">Spinning up ephemeral convo…</p>}
          {convo.kind === "error"    && <p className="text-xs text-status-error">{convo.message}</p>}
          {convo.kind === "idle" && agentId === null && (
            <p className="text-xs text-app-muted">Pick an agent above to begin.</p>
          )}
          {convo.kind === "ready" && messages.length === 0 && (
            <p className="text-xs text-app-muted">Type as the user — the agent's reply streams back live.</p>
          )}
          {messages.length > 0 && <DaySeparator />}
          {messages.map((m) => (
            <ChatMessageBubble
              key={m.id}
              msg={withIdentity(m, activeAgent, currentUser)}
              userLabel="You (as user)"
              assistantLabel={activeAgent?.name ?? "Agent"}
            />
          ))}
          {pendingReply && <ChatThinkingIndicator label={activeAgent?.name ?? "Agent"} />}
        </div>

        <footer className="border-t border-border p-3">
          <div className="flex gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              placeholder={convo.kind === "ready" ? "Type as the user…" : "Setting up…"}
              rows={2}
              disabled={convo.kind !== "ready"}
              className="flex-1"
            />
            <Button
              type="button" variant="red" size="md"
              onClick={send}
              disabled={convo.kind !== "ready" || !draft.trim() || sending}
              className="self-end"
            >
              <Send size={16} strokeWidth={2} />
            </Button>
          </div>
        </footer>
      </Card>
    </div>
  )
}

function DaySeparator() {
  const today = new Date().toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })
  return (
    <div className="flex items-center gap-3 text-[10px] uppercase tracking-wide text-app-subtle">
      <span className="h-px flex-1 bg-border" />
      <span>{today}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

// Decorate a ChatBubble with the matching author's name + avatar URL so
// the bubble renders the operator on the user side and the active agent on
// the assistant side. Pure projection — no state.
function withIdentity(
  m: ChatBubble,
  agent: TestLabAgentOption | null,
  user: SharedProps["current_user"],
): ChatBubble {
  if (m.role === "user" && user) {
    return { ...m, authorName: user.name ?? user.email, avatarUrl: user.avatarUrl ?? null }
  }
  if (m.role === "assistant" && agent) {
    return { ...m, authorName: agent.name, avatarUrl: agent.avatarUrl ?? null }
  }
  return m
}

function ScopeToggle({ value, onChange }: { value: "skill_only" | "all_skills"; onChange: (v: "skill_only" | "all_skills") => void }) {
  return (
    <div className="inline-flex overflow-hidden rounded-control border border-border text-[11px]">
      <button
        type="button"
        onClick={() => onChange("skill_only")}
        className={cn("px-2 py-1", value === "skill_only" ? "bg-brand-red text-white" : "bg-app-panel2 text-app-muted hover:text-app-text")}
      >
        Only this skill
      </button>
      <button
        type="button"
        onClick={() => onChange("all_skills")}
        className={cn("px-2 py-1", value === "all_skills" ? "bg-brand-red text-white" : "bg-app-panel2 text-app-muted hover:text-app-text")}
      >
        All skills
      </button>
    </div>
  )
}

async function refetchMessages(skillId: number, convoId: number): Promise<ChatBubble[]> {
  const res = await fetch(`/skills/${skillId}/test/agent_chat/messages?convo_id=${convoId}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  })
  if (!res.ok) return []
  const data = (await res.json()) as { messages: ChatBubble[] }
  return data.messages
}

function csrfHeaders(): HeadersInit {
  const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
  return {
    "X-CSRF-Token": token ?? "",
    "Content-Type": "application/x-www-form-urlencoded",
    "Accept": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  }
}
